/**
 * Cart Drawer Functionality
 * Handles cart drawer open/close, cart item loading, and add to cart functionality
 */

(function() {
  'use strict';
  
  // Get configuration from global object
  const config = window.SHOPIFY_CONFIG || {};
  const i18n = config.translations || {};
  const quantityLabel = i18n.quantityLabel || 'Quantity';
  const requestClient = window.themeRequest || {
    fetchJSON: function(url, options) {
      return fetch(url, options).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      });
    }
  };
  let loadItemsToken = 0;
  const FOCUSABLE_SELECTOR = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let drawerFocusTrapHandler = null;
  let drawerFocusReturnTarget = null;
  
  /**
   * Escape HTML to prevent XSS attacks
   * @param {string} text - The text to escape
   * @returns {string} - The escaped HTML-safe string
   */
  const escapeHtml = function(text) {
    if (text === null || text === undefined) {
      return '';
    }
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  };
  
  // Format money safely
  const formatMoney = function(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    if (config.moneyFormat) {
      const value = (cents / 100).toFixed(2);
      return config.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, value);
    }
    return `£${(cents / 100).toFixed(2)}`;
  };

  const isVisibleElement = function(element) {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  };

  const getFocusableElements = function(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(element => isVisibleElement(element) && element.getAttribute('aria-hidden') !== 'true');
  };

  const activateDrawerFocusTrap = function(drawer, content) {
    if (!drawer || !content) return;

    if (drawerFocusTrapHandler) {
      document.removeEventListener('keydown', drawerFocusTrapHandler, true);
      drawerFocusTrapHandler = null;
    }

    if (!content.hasAttribute('tabindex')) {
      content.setAttribute('tabindex', '-1');
    }

    const focusableElements = getFocusableElements(content);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    } else {
      content.focus();
    }

    drawerFocusTrapHandler = function(event) {
      if (drawer.classList.contains('hidden')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        drawerApi.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = getFocusableElements(content);
      if (items.length === 0) {
        event.preventDefault();
        content.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !content.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last || !content.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', drawerFocusTrapHandler, true);
  };

  const deactivateDrawerFocusTrap = function() {
    if (drawerFocusTrapHandler) {
      document.removeEventListener('keydown', drawerFocusTrapHandler, true);
      drawerFocusTrapHandler = null;
    }

    if (
      drawerFocusReturnTarget &&
      typeof drawerFocusReturnTarget.focus === 'function' &&
      drawerFocusReturnTarget.isConnected
    ) {
      drawerFocusReturnTarget.focus({ preventScroll: true });
    }
    drawerFocusReturnTarget = null;
  };
  
  // Cart drawer functions - exposed globally
  const drawerApi = {
    open: function() {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer) {
        console.error('Cart drawer elements not found');
        return;
      }
      const content = drawer.querySelector('.cart-drawer-content');
      if (!content) {
        console.error('Cart drawer elements not found');
        return;
      }

      if (drawer.classList.contains('hidden')) {
        drawerFocusReturnTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      }
      
      drawer.classList.remove('hidden');
      setTimeout(() => {
        content.classList.remove('translate-x-full');
        activateDrawerFocusTrap(drawer, content);
      }, 10);
      
      // Load cart items
      this.loadItems();
    },
    
    close: function() {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer) return;
      const content = drawer.querySelector('.cart-drawer-content');
      if (!content) return;
      
      content.classList.add('translate-x-full');
      deactivateDrawerFocusTrap();
      setTimeout(() => {
        drawer.classList.add('hidden');
      }, 300);
    },
    
    loadItems: function() {
      const currentLoadToken = ++loadItemsToken;
      const cartItems = document.getElementById('cart-items');
      const emptyCart = document.getElementById('empty-cart');
      const cartTotal = document.getElementById('cart-total');
      const cartLoading = document.getElementById('cart-loading');
      const cartError = document.getElementById('cart-error');
      
      // Show loading state
      if (cartLoading) cartLoading.classList.remove('hidden');
      if (cartItems) cartItems.classList.add('hidden');
      if (emptyCart) emptyCart.classList.add('hidden');
      if (cartError) cartError.classList.add('hidden');
      
      return requestClient.fetchJSON('/cart.js', { retry: 1, timeoutMs: 12000 })
        .then(cart => {
          if (currentLoadToken !== loadItemsToken) {
            return cart;
          }

          if (!cartItems || !emptyCart || !cartTotal) {
            console.error('Cart drawer elements not found');
            return;
          }
          
          // Hide loading state
          if (cartLoading) cartLoading.classList.add('hidden');
          
          if (cart.items.length === 0) {
            cartItems.innerHTML = '';
            cartItems.classList.add('hidden');
            emptyCart.classList.remove('hidden');
          } else {
            emptyCart.classList.add('hidden');
            cartItems.classList.remove('hidden');
            cartItems.innerHTML = cart.items.map(item => {
              // Build properties HTML if they exist (with XSS protection)
              let propertiesHtml = '';
              if (item.properties && Object.keys(item.properties).length > 0) {
                propertiesHtml = Object.entries(item.properties)
                  .filter(([key, value]) => !key.startsWith('_') && value && value !== '')
                  .map(([key, value]) => `
                    <p class="text-sm text-forest font-medium">
                      ${escapeHtml(key)}: <span class="text-sage font-normal">${escapeHtml(value)}</span>
                    </p>
                  `).join('');
              }
              
              // Build variant options HTML with names (with XSS protection)
              let variantOptionsHtml = '';
              if (item.options_with_values && item.options_with_values.length > 0) {
                // Filter out "Default Title" which appears for products with no variants
                const validOptions = item.options_with_values.filter(opt => opt.value !== 'Default Title');
                if (validOptions.length > 0) {
                  variantOptionsHtml = validOptions
                    .map(opt => `
                      <p class="text-sm text-sage">
                        ${escapeHtml(opt.name)}: <span class="font-medium">${escapeHtml(opt.value)}</span>
                      </p>
                    `).join('');
                }
              }

              // Escape all user-controlled data to prevent XSS
              const safeImage = escapeHtml(item.image || '');
              const safeTitle = escapeHtml(item.title);
              const safeQuantity = escapeHtml(String(item.quantity));

              return `
                <div class="flex items-center gap-4 py-4 border-b">
                  <img src="${safeImage}" alt="${safeTitle}" class="w-16 h-16 object-cover rounded" width="64" height="64" loading="lazy">
                  <div class="flex-1">
                    <h4 class="font-semibold text-forest">${safeTitle}</h4>
                    ${variantOptionsHtml}
                    ${propertiesHtml}
                    <p class="text-sage">${quantityLabel}: ${safeQuantity}</p>
                  </div>
                  <div class="text-right">
                    <p class="font-semibold text-forest">${formatMoney(item.line_price)}</p>
                  </div>
                </div>
              `;
            }).join('');
          }
          
          cartTotal.textContent = formatMoney(cart.total_price);

          // Populate cart drawer note with current cart note
          const cartDrawerNote = document.getElementById('cart-drawer-note');
          if (cartDrawerNote) {
            cartDrawerNote.value = cart.note || '';
          }
          
          // Initialize cart drawer note listener
          this.initializeNote();
          return cart;
        })
        .catch(error => {
          console.error('Error loading cart items:', error);
          
          // Hide loading state and show error
          if (cartLoading) cartLoading.classList.add('hidden');
          if (cartItems) cartItems.classList.add('hidden');
          if (emptyCart) emptyCart.classList.add('hidden');
          const errorMessage = cartError ? cartError.querySelector('p') : null;
          if (errorMessage) {
            errorMessage.textContent = i18n.cartLoadError || 'Unable to load basket. Please try again.';
          }
          if (cartError) cartError.classList.remove('hidden');
          throw error;
        });
    },
    
    addToCart: function(variantId, quantity = 1, properties = {}) {
      const requestBody = {
        items: [{
          id: variantId,
          quantity: quantity,
          properties: properties
        }]
      };
      
      return requestClient.fetchJSON('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        retry: 0,
        timeoutMs: 10000
      })
      .then(() => {
        this.open();
        
        // Update cart count in header
        if (window.cart && window.cart.updateCartCount) {
          window.cart.updateCartCount();
        }
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        throw error;
      });
    },

    refresh: function() {
      return this.loadItems();
    },
    
    updateNote: function(note) {
      requestClient.fetchJSON('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: note
        }),
        retry: 0,
        timeoutMs: 10000
      })
      .then(() => {})
      .catch(error => {
        console.error('Error updating cart note:', error);
      });
    },
    
    initializeNote: function() {
      const cartDrawerNoteTextarea = document.getElementById('cart-drawer-note');

      if (cartDrawerNoteTextarea && !cartDrawerNoteTextarea.dataset.initialized) {
        cartDrawerNoteTextarea.dataset.initialized = 'true';
        let cartDrawerNoteTimeout;

        cartDrawerNoteTextarea.addEventListener('input', function() {
          // Clear the previous timeout
          clearTimeout(cartDrawerNoteTimeout);

          // Set a new timeout to update the cart note after 1 second of inactivity
          cartDrawerNoteTimeout = setTimeout(() => {
            drawerApi.updateNote(this.value);
          }, 1000);
        });
      }
    }
  };

  window.cartDrawer = drawerApi;
  
  // Event delegation for cart drawer actions (CSP compliant)
  const initCartDrawerEvents = function() {
    document.addEventListener('click', function(e) {
      const target = e.target.closest('[data-action]');
      if (!target) return;
      
      const action = target.getAttribute('data-action');
      
      switch (action) {
        case 'close-cart-drawer':
          drawerApi.close();
          break;
        case 'retry-load-cart':
          drawerApi.loadItems();
          break;
      }
    });
  };
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawerEvents);
  } else {
    initCartDrawerEvents();
  }
})();
